import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { invalidateXtPriceCache } from '../../hooks/useXtPrice';
import { useNotify } from '../../context/NotificationContext';
import { parseUSDT } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';

function pctToBps(pctStr) {
  const n = Number(pctStr);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function bpsToPctStr(bps) {
  const n = Number(bps) / 100;
  return String(parseFloat(n.toFixed(2)));
}

// Convert seconds ↔ {value, unit} for the ROI timeframe picker
const UNITS = {
  seconds: 1,
  minutes: 60,
  hours:   3600,
  days:    86400,
};

function secondsToVU(sec) {
  const s = Number(sec) || 0;
  if (s === 0) return { value: '0', unit: 'seconds' };
  if (s % UNITS.days === 0)    return { value: String(s / UNITS.days),    unit: 'days' };
  if (s % UNITS.hours === 0)   return { value: String(s / UNITS.hours),   unit: 'hours' };
  if (s % UNITS.minutes === 0) return { value: String(s / UNITS.minutes), unit: 'minutes' };
  return { value: String(s), unit: 'seconds' };
}

function vuToSeconds(value, unit) {
  const n = Number(value);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * (UNITS[unit] || 1));
}

function humanSec(sec) {
  const s = Number(sec);
  if (!s) return '—';
  if (s % UNITS.days === 0)    return `${s / UNITS.days} day${s === UNITS.days ? '' : 's'}`;
  if (s % UNITS.hours === 0)   return `${s / UNITS.hours} hour${s === UNITS.hours ? '' : 's'}`;
  if (s % UNITS.minutes === 0) return `${s / UNITS.minutes} minute${s === UNITS.minutes ? '' : 's'}`;
  return `${s} seconds`;
}

export default function SettingsPanel() {
  const { read, write } = useContracts();
  const notify = useNotify();

  const [current, setCurrent] = useState({
    xtPrice: '', withdrawFeeUsdtPct: '', withdrawFeeXtPct: '',
    minWithdrawUsdt: '', minWithdrawXt: '',
    roiTimeframeSec: '0',
    defaultRef: '',
  });
  const [values, setValues] = useState({
    xtPrice: '', withdrawFeeUsdtPct: '', withdrawFeeXtPct: '',
    minWithdrawUsdt: '', minWithdrawXt: '',
    roiValue: '1', roiUnit: 'days',
    defaultRef: '',
  });
  const [busy, setBusy] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      read.storage.xtPriceUSDT().catch(() => ethers.BigNumber.from(0)),
      read.storage.withdrawFeeUsdtBps().catch(() => ethers.BigNumber.from(0)),
      read.storage.withdrawFeeXtBps().catch(() => ethers.BigNumber.from(0)),
      read.storage.minWithdrawUsdtUSDT().catch(() => ethers.BigNumber.from(0)),
      read.storage.minWithdrawXtUSDT().catch(() => ethers.BigNumber.from(0)),
      read.storage.roiTimeframe().catch(() => ethers.BigNumber.from(86400)),
      read.storage.defaultReferrerId().catch(() => ethers.BigNumber.from(0)),
    ]).then(([xt, feeU, feeX, minU, minX, roi, dref]) => {
      if (cancelled) return;
      const roiSec = roi.toString();
      const vu = secondsToVU(roiSec);
      setCurrent({
        xtPrice: ethers.utils.formatUnits(xt, 18),
        withdrawFeeUsdtPct: bpsToPctStr(feeU),
        withdrawFeeXtPct: bpsToPctStr(feeX),
        minWithdrawUsdt: ethers.utils.formatUnits(minU, 18),
        minWithdrawXt:   ethers.utils.formatUnits(minX, 18),
        roiTimeframeSec: roiSec,
        defaultRef: dref.toString(),
      });
      setValues({
        xtPrice: ethers.utils.formatUnits(xt, 18),
        withdrawFeeUsdtPct: bpsToPctStr(feeU),
        withdrawFeeXtPct: bpsToPctStr(feeX),
        minWithdrawUsdt: ethers.utils.formatUnits(minU, 18),
        minWithdrawXt:   ethers.utils.formatUnits(minX, 18),
        roiValue: vu.value,
        roiUnit: vu.unit,
        defaultRef: dref.toString(),
      });
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(key, fn, successMsg) {
    if (!write) return notify.error('Wallet not connected.');
    setBusy(key);
    try {
      const tx = await fn();
      notify.info('Transaction submitted...');
      await tx.wait();
      notify.success(successMsg);
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  const feeUsdtNum = Number(values.withdrawFeeUsdtPct);
  const feeUsdtInvalid = !isFinite(feeUsdtNum) || feeUsdtNum < 0 || feeUsdtNum > 30;
  const feeXtNum = Number(values.withdrawFeeXtPct);
  const feeXtInvalid = !isFinite(feeXtNum) || feeXtNum < 0 || feeXtNum > 30;

  const roiNewSec = vuToSeconds(values.roiValue, values.roiUnit);
  const roiChanged = String(roiNewSec) !== current.roiTimeframeSec;
  const roiInvalid = roiNewSec < 1 || roiNewSec > 30 * UNITS.days;

  return (
    <div className="admin-panel">
      <h3>Global Settings</h3>
      <p className="panel-hint">Tune platform-wide parameters. Changes take effect immediately.</p>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">XT Price (USDT per XT)</label>
            <input className="input" type="text" value={values.xtPrice}
              onChange={(e) => setValues({ ...values, xtPrice: e.target.value })} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'xt' || values.xtPrice === current.xtPrice}
          onClick={() => run('xt',
            async () => {
              const tx = await write.storage.setXTPrice(parseUSDT(values.xtPrice));
              // Invalidate the shared cache after tx sent (the run() wrapper awaits its receipt)
              tx.wait().then(() => invalidateXtPriceCache()).catch(() => {});
              return tx;
            },
            'XT price updated'
          )}>
          {busy === 'xt' ? <Spinner /> : 'Update'}
        </button>
      </div>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">USDT Withdraw Fee (%) — max 30%</label>
            <div style={{ position: 'relative' }}>
              <input className="input" type="text" value={values.withdrawFeeUsdtPct}
                onChange={(e) => setValues({ ...values, withdrawFeeUsdtPct: e.target.value.replace(/[^0-9.]/g, '') })}
                placeholder="10"
              />
              <span style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 14,
                pointerEvents: 'none',
              }}>%</span>
            </div>
            {feeUsdtInvalid && values.withdrawFeeUsdtPct && (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--error)' }}>
                Must be between 0 and 30
              </div>
            )}
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'feeU' || feeUsdtInvalid || values.withdrawFeeUsdtPct === current.withdrawFeeUsdtPct}
          onClick={() => run('feeU',
            () => write.storage.setWithdrawFeeUsdt(pctToBps(values.withdrawFeeUsdtPct)),
            'USDT withdraw fee updated'
          )}>
          {busy === 'feeU' ? <Spinner /> : 'Update'}
        </button>
      </div>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">XT Withdraw Fee (%) — max 30%</label>
            <div style={{ position: 'relative' }}>
              <input className="input" type="text" value={values.withdrawFeeXtPct}
                onChange={(e) => setValues({ ...values, withdrawFeeXtPct: e.target.value.replace(/[^0-9.]/g, '') })}
                placeholder="10"
              />
              <span style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 14,
                pointerEvents: 'none',
              }}>%</span>
            </div>
            {feeXtInvalid && values.withdrawFeeXtPct && (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--error)' }}>
                Must be between 0 and 30
              </div>
            )}
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'feeX' || feeXtInvalid || values.withdrawFeeXtPct === current.withdrawFeeXtPct}
          onClick={() => run('feeX',
            () => write.storage.setWithdrawFeeXt(pctToBps(values.withdrawFeeXtPct)),
            'XT withdraw fee updated'
          )}>
          {busy === 'feeX' ? <Spinner /> : 'Update'}
        </button>
      </div>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Min USDT Withdrawal (USDT)</label>
            <input className="input" type="text" value={values.minWithdrawUsdt}
              onChange={(e) => setValues({ ...values, minWithdrawUsdt: e.target.value })} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'minU' || values.minWithdrawUsdt === current.minWithdrawUsdt}
          onClick={() => run('minU',
            () => write.storage.setMinWithdrawUsdt(parseUSDT(values.minWithdrawUsdt)),
            'Min USDT withdrawal updated'
          )}>
          {busy === 'minU' ? <Spinner /> : 'Update'}
        </button>
      </div>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Min XT Withdrawal (USDT equivalent)</label>
            <input className="input" type="text" value={values.minWithdrawXt}
              onChange={(e) => setValues({ ...values, minWithdrawXt: e.target.value })} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'minX' || values.minWithdrawXt === current.minWithdrawXt}
          onClick={() => run('minX',
            () => write.storage.setMinWithdrawXt(parseUSDT(values.minWithdrawXt)),
            'Min XT withdrawal updated'
          )}>
          {busy === 'minX' ? <Spinner /> : 'Update'}
        </button>
      </div>

      {/* ROI Timeframe = mining click cooldown */}
      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Mining Cooldown (ROI Timeframe)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 8 }}>
              <input
                className="input"
                type="text"
                value={values.roiValue}
                onChange={(e) => setValues({ ...values, roiValue: e.target.value.replace(/[^0-9.]/g, '') })}
                placeholder="1"
              />
              <select
                className="select"
                value={values.roiUnit}
                onChange={(e) => setValues({ ...values, roiUnit: e.target.value })}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              Current on-chain: <strong style={{ color: 'var(--accent)' }}>{humanSec(current.roiTimeframeSec)}</strong>
              {' · '}Will send: <strong style={{ color: 'var(--accent)' }}>{roiNewSec} seconds</strong>
            </div>
            {roiInvalid && (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--error)' }}>
                Must be between 1 second and 30 days
              </div>
            )}
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'roi' || roiInvalid || !roiChanged}
          onClick={() => run('roi',
            () => write.storage.setROITimeframe(roiNewSec),
            'Mining cooldown updated'
          )}>
          {busy === 'roi' ? <Spinner /> : 'Update'}
        </button>
      </div>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Default Referrer ID (owner only)</label>
            <input className="input" type="text" value={values.defaultRef}
              onChange={(e) => setValues({ ...values, defaultRef: e.target.value.replace(/\D/g, '') })} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'dref' || values.defaultRef === current.defaultRef}
          onClick={() => run('dref',
            () => write.storage.setDefaultReferrer(values.defaultRef),
            'Default referrer updated'
          )}>
          {busy === 'dref' ? <Spinner /> : 'Update'}
        </button>
      </div>
    </div>
  );
}
